import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUserGroup } from './queryKeys';

export type UserGroupData = {
  name: string;
  users?: UUID[];
  memberUserGroups?: UUID[];
  parentUserGroups?: UUID[];
  orgUnits: UUID[];
  orgUnitPaths?: UUID[][];
  userGroupPaths?: UUID[][];
};

type UserGroupCreateResponse = {
  code: number;
  message: string;
  id: UUID;
};

type CreateUserGroupMutationOptions = UseMutationOptions<
  UserGroupCreateResponse,
  Error,
  UserGroupData
>;

/**
 * Creates a new user group.
 *
 * @param {Object} userGroupData - An object containing the user group data to create.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Access-Control/operation/create_user_group_access_control_user_group_post
 */
export const createUserGroup = async (userGroupData: UserGroupData) => {
  // Temporary hack: rename childUserGroups to memberUserGroups;
  // childUserGroups would be the consistent name, but the API has not been adjusted yet
  const hackedUserGroupData = Object.fromEntries(
    Object.entries(userGroupData).map(([key, value]) => {
      return key === 'childUserGroups'
        ? ['memberUserGroups', value]
        : [key, value];
    }),
  ) as UserGroupData;

  const response = await vestigasApi
    .post(ENDPOINT.USER_GROUP.CREATE(), {
      json: hackedUserGroupData,
    })
    .json<UserGroupCreateResponse>();

  return response;
};

/**
 * Custom hook for creating a new user group.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateUserGroup = (
  options?: CreateUserGroupMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userGroupData: UserGroupData) =>
      createUserGroup(userGroupData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysUserGroup.getAll({}),
      });
    },
    ...options,
  });
};
